import { Alert, Stack, Typography } from '@mui/material';
import { BulkDeleteVisitorsPayload, BulkOperationError, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useBulkUserActions from 'v2/hooks/useBulkUserActions/useBulkUserActions';
import { getSelectedUserDescription } from '../../components/selectedUsersUtils';
import BulkUserActionFailuresModal from '../BulkUserActionFailuresModal/BulkUserActionFailuresModal';
import { SelectedUser } from '../BulkUserActionFailuresModal/bulkUserActionErrorsUtils';
import { getDeletableUsers, hasDeletedAllVisitors } from './deleteUsersUtils';

interface Props {
  selectedVisitors: Visitor[];
  onSuccess: () => void;
}

const DeleteUsersModal: React.FC<Props> = ({ selectedVisitors, onSuccess }) => {
  const { translation } = useTranslation();
  const { closeModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const [errors, setErrors] = useState<BulkOperationError[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const { bulkDeleteVisitors, bulkDeleteVisitorsLoading } = useBulkUserActions({
    translation,
    handleFetchError: setErrorMessage
  });
  const { deletableVisitorIds, entraUsersCount } = getDeletableUsers(selectedVisitors);
  const deletableUsersCount = deletableVisitorIds.length;
  const selectedUsers: SelectedUser[] = selectedVisitors
    .filter(({ id }) => deletableVisitorIds.includes(id))
    .map(({ id, name }) => ({ id, name }));

  const handleSuccess = () => {
    showSnackbar(translation.users_deleted, { severity: 'success' });
    onSuccess();
    closeModal();
  };

  const handleResult = (payload: BulkDeleteVisitorsPayload, visitorIds: string[]) => {
    if (hasDeletedAllVisitors(payload, visitorIds)) {
      handleSuccess();
      return;
    }

    setErrors(
      payload.errors.length > 0
        ? payload.errors
        : visitorIds
            .filter((visitorId) => !payload.deletedVisitorIds.includes(visitorId))
            .map((visitorId) => ({ visitorId }) as BulkOperationError)
    );
  };

  const handleDelete = async () => {
    setErrorMessage(undefined);
    const payload = await bulkDeleteVisitors({ visitorIds: deletableVisitorIds });

    if (payload) handleResult(payload, deletableVisitorIds);
  };

  const handleRetry = async (failedVisitorIds: string[]) => {
    setErrorMessage(undefined);
    const payload = await bulkDeleteVisitors({ visitorIds: failedVisitorIds });

    if (payload) handleResult(payload, failedVisitorIds);
  };

  if (errors.length > 0) {
    return (
      <BulkUserActionFailuresModal
        errors={errors}
        selectedUsers={selectedUsers}
        title={translation.bulk_action_failures_title}
        loading={bulkDeleteVisitorsLoading}
        errorMessage={errorMessage}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <ModalTemplate
      id="delete-users-modal"
      title={translation.delete_users_title}
      primaryAction={{
        id: 'confirm-delete-users-button',
        label: translation.delete_users,
        onClick: handleDelete,
        dangerzone: true,
        loading: bulkDeleteVisitorsLoading,
        disabled: deletableUsersCount === 0 || bulkDeleteVisitorsLoading
      }}
      secondaryAction={{ id: 'cancel-delete-users-button', label: translation.cancel }}
    >
      <Stack gap={3}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <Stack direction="row" gap={8}>
          <Stack gap={0.5}>
            <Typography variant="h4">{deletableUsersCount}</Typography>
            <Typography variant="body2" color="textSecondary">
              {translation.can_be_deleted}
            </Typography>
          </Stack>
          <Stack gap={0.5}>
            <Typography variant="h4">{entraUsersCount}</Typography>
            <Typography variant="body2" color="textSecondary">
              {translation.from_entra_id}
            </Typography>
          </Stack>
        </Stack>
        {entraUsersCount > 0 && (
          <Alert severity="warning">
            <Stack gap={1}>
              <Typography variant="subtitle1">{translation.cannot_delete_entra_users}</Typography>
              <Typography>{translation.entra_deletion_info}</Typography>
            </Stack>
          </Alert>
        )}
        {deletableUsersCount > 0 && (
          <Typography variant="body2">
            {getSelectedUserDescription(deletableUsersCount, translation.users_delete_confirmation)}
          </Typography>
        )}
      </Stack>
    </ModalTemplate>
  );
};

export default DeleteUsersModal;
